import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, Inbox, MessagesSquare, RotateCcw } from 'lucide-react';
import { api, ErroApi } from '../../api/cliente';
import type {
  ConversaAdmin,
  ConversaAdminResumo,
  StatusConversa,
} from '../../api/tipos';
import { Chat } from '../../componentes/Chat';
import { Pagina, TituloPagina } from '../../componentes/Pagina';
import {
  Abas,
  Aviso,
  Botao,
  Cartao,
  cx,
  Esqueleto,
  Etiqueta,
  Vazio,
} from '../../componentes/ui';
import { dataHora, plural, reais, tempoRelativo } from '../../util/formato';
import css from './SuporteAdmin.module.css';

const FILTROS = [
  { valor: 'ABERTA', rotulo: 'Abertas' },
  { valor: 'FECHADA', rotulo: 'Encerradas' },
  { valor: 'todas', rotulo: 'Todas' },
] as const;

type Filtro = (typeof FILTROS)[number]['valor'];

const INTERVALO_CONVERSA_MS = 8_000;
const INTERVALO_LISTA_MS = 15_000;

/**
 * Atendimento: todas as conversas de suporte, com o contexto do cliente ao
 * lado do fio.
 *
 * "Aguardando" = a última mensagem é do cliente, ou seja, a vez é nossa. É o
 * que ordena o trabalho, mais do que "não lida" — uma conversa lida e não
 * respondida continua pendente.
 */
export function SuporteAdmin() {
  const [params, setParams] = useSearchParams();
  const selecionada = params.get('c');
  const [filtro, setFiltro] = useState<Filtro>('ABERTA');
  const [lista, setLista] = useState<ConversaAdminResumo[] | null>(null);
  const [conversa, setConversa] = useState<ConversaAdmin | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const carregarLista = useCallback(async () => {
    const q = filtro === 'todas' ? '' : `?status=${filtro}`;
    try {
      setLista(await api<ConversaAdminResumo[]>(`/admin/suporte${q}`));
    } catch (err) {
      setErro(
        err instanceof ErroApi ? err.message : 'Falha ao carregar o suporte.',
      );
    }
  }, [filtro]);

  useEffect(() => {
    void carregarLista();
    const t = setInterval(() => void carregarLista(), INTERVALO_LISTA_MS);
    return () => clearInterval(t);
  }, [carregarLista]);

  useEffect(() => {
    setConversa(null);
    if (!selecionada) return;
    let vivo = true;
    const buscar = () =>
      api<ConversaAdmin>(`/admin/suporte/${selecionada}`)
        .then((c) => vivo && setConversa(c))
        .catch(() => undefined);
    void buscar();
    const t = setInterval(() => void buscar(), INTERVALO_CONVERSA_MS);
    return () => {
      vivo = false;
      clearInterval(t);
    };
  }, [selecionada]);

  async function mudarStatus(status: StatusConversa) {
    if (!conversa) return;
    try {
      setConversa(
        await api<ConversaAdmin>(`/admin/suporte/${conversa.id}`, {
          metodo: 'PATCH',
          corpo: { status },
        }),
      );
      void carregarLista();
    } catch (err) {
      setErro(err instanceof ErroApi ? err.message : 'Falha ao atualizar.');
    }
  }

  const aguardando = lista?.filter((c) => c.aguardandoResposta).length ?? 0;

  return (
    <Pagina largura="larga">
      <TituloPagina
        titulo="Suporte"
        descricao={
          lista
            ? `${plural(aguardando, 'conversa')} aguardando resposta.`
            : 'Conversas dos clientes.'
        }
        acoes={
          <Abas rotulo="Situação da conversa" opcoes={FILTROS} atual={filtro} aoMudar={setFiltro} />
        }
      />

      {erro && <Aviso>{erro}</Aviso>}

      <div className={css.grade}>
        <Cartao titulo="Conversas" padding={0}>
          {!lista ? (
            <div className={css.carregando}>
              <Esqueleto altura={48} />
              <Esqueleto altura={48} />
              <Esqueleto altura={48} />
            </div>
          ) : lista.length === 0 ? (
            <Vazio
              icone={<Inbox />}
              titulo="Nenhuma conversa"
              descricao="Nenhuma conversa neste filtro."
            />
          ) : (
            <ul className={css.lista}>
              {lista.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => setParams({ c: c.id })}
                    className={css.item}
                    aria-current={c.id === selecionada ? 'true' : undefined}
                  >
                    <span className={css.itemTopo}>
                      <span className={cx(css.itemNome, c.naoLidas > 0 && css.naoLida)}>
                        {c.user.nome}
                      </span>
                      {c.aguardandoResposta && c.status === 'ABERTA' && (
                        <Etiqueta tom={c.naoLidas > 0 ? 'alerta' : 'neutro'}>
                          {c.naoLidas > 0 ? plural(c.naoLidas, 'nova') : 'Aguardando'}
                        </Etiqueta>
                      )}
                    </span>
                    <span className={css.itemAssunto}>{c.assunto}</span>
                    <span className={css.itemPrevia}>
                      {tempoRelativo(c.ultimaMensagemEm)} · {c.previa}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Cartao>

        {!selecionada ? (
          <Cartao>
            <Vazio
              icone={<MessagesSquare />}
              titulo="Nenhuma conversa aberta"
              descricao="Escolha uma conversa na lista para ver o histórico e responder."
            />
          </Cartao>
        ) : !conversa ? (
          <Cartao>
            <div className={css.carregandoConversa}>
              <Esqueleto altura={20} largura="40%" />
              <Esqueleto altura={56} />
              <Esqueleto altura={200} />
            </div>
          </Cartao>
        ) : (
          <Cartao
            titulo={conversa.assunto}
            acoes={
              conversa.status === 'ABERTA' ? (
                <Botao
                  pequeno
                  aparencia="secundario"
                  icone={<CheckCircle2 />}
                  onClick={() => void mudarStatus('FECHADA')}
                >
                  Encerrar
                </Botao>
              ) : (
                <Botao
                  pequeno
                  aparencia="secundario"
                  icone={<RotateCcw />}
                  onClick={() => void mudarStatus('ABERTA')}
                >
                  Reabrir
                </Botao>
              )
            }
          >
            <div className={css.conversa}>
              <ContextoCliente user={conversa.user} />

              <Chat
                mensagens={conversa.mensagens}
                euSou="SUPORTE"
                nomeOutroLado={conversa.user.nome}
                aviso={
                  conversa.status === 'FECHADA'
                    ? 'Conversa encerrada. Responder não a reabre; o cliente escrevendo, sim.'
                    : undefined
                }
                aoEnviar={async (texto) => {
                  setConversa(
                    await api<ConversaAdmin>(
                      `/admin/suporte/${conversa.id}/mensagens`,
                      { metodo: 'POST', corpo: { texto } },
                    ),
                  );
                  void carregarLista();
                }}
              />
            </div>
          </Cartao>
        )}
      </div>
    </Pagina>
  );
}

/** Quem é o cliente, para responder sem abrir outra tela. */
function ContextoCliente({ user }: { user: ConversaAdmin['user'] }) {
  const linha = (rotulo: string, valor: string) => (
    <span key={rotulo}>
      <span className={css.contextoRotulo}>{rotulo}: </span>
      {valor}
    </span>
  );
  return (
    <div className={css.contexto}>
      {linha('E-mail', user.email)}
      {linha('Documento', user.documento)}
      {user.telefone && linha('Telefone', user.telefone)}
      {linha('Saldo', reais(user.carteira?.saldoCentavos ?? 0))}
      {(user.carteira?.reservadoCentavos ?? 0) > 0 &&
        linha('Reservado', reais(user.carteira?.reservadoCentavos ?? 0))}
      {linha('Cliente desde', dataHora(user.createdAt))}
    </div>
  );
}
